import { useEffect, useRef, useState, useCallback } from "react";
import { MessageCircle, X, Send, ImagePlus, Loader2, Check } from "lucide-react";
import { api } from "@/lib/api";

const SID_KEY = "ptwa_shikin_sid";
const MSG_KEY = "ptwa_shikin_msgs";

const GREETING = "Hi! I'm Shikin from Perth Tiling WA. How can I help you with your tiling project today?";
const QUICK_REPLIES = [
  "I need my bathroom tiled",
  "Kitchen splashback",
  "Get a free quote",
  "Speak to the team",
];

function newId() {
  try {
    if (crypto?.randomUUID) return crypto.randomUUID();
  } catch (e) { /* noop */ }
  return `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export default function ShikinChat() {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [input, setInput] = useState("");
  const [sid] = useState(() => {
    let s = localStorage.getItem(SID_KEY);
    if (!s) { s = newId(); localStorage.setItem(SID_KEY, s); }
    return s;
  });
  const [messages, setMessages] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(MSG_KEY) || "null");
      if (Array.isArray(saved) && saved.length) return saved;
    } catch (e) { /* noop */ }
    return [{ role: "assistant", content: GREETING }];
  });
  const scrollRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(MSG_KEY, JSON.stringify(messages.slice(-40)));
  }, [messages]);

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  useEffect(() => { if (open) setTimeout(scrollToBottom, 60); }, [messages, open, sending, scrollToBottom]);

  const push = (m) => setMessages((prev) => [...prev, m]);

  const sendText = useCallback(async (text) => {
    const trimmed = (text || "").trim();
    if (!trimmed || sending) return;
    push({ role: "user", content: trimmed });
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/chat/shikin", { session_id: sid, message: trimmed.slice(0, 2000) });
      push({ role: "assistant", content: data.reply });
    } catch (e) {
      const detail = e.response?.data?.detail;
      push({ role: "assistant", content: typeof detail === "string" ? detail : "Sorry, I couldn't get through just then. Could you try that again in a moment?" });
    } finally {
      setSending(false);
    }
  }, [sid, sending]);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("session_id", sid);
      files.slice(0, 8).forEach((f) => fd.append("files", f));
      const { data } = await api.post("/chat/shikin/upload", fd);
      const n = data.uploaded || 0;
      if (n > 0) {
        push({ role: "user", content: `${n} photo${n > 1 ? "s" : ""} uploaded`, _photos: true });
        await sendText(`I've uploaded ${n} photo${n > 1 ? "s" : ""} of the area.`);
      } else {
        push({ role: "assistant", content: "Hmm, those didn't upload — please make sure they're image files under 10MB and try again." });
      }
    } catch (err) {
      push({ role: "assistant", content: "Sorry, the photo upload didn't go through. You can try again or continue without photos." });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const showQuick = messages.length <= 1 && !sending;

  return (
    <>
      {/* Launcher */}
      {!open && (
        <button
          data-testid="shikin-launcher"
          onClick={() => setOpen(true)}
          aria-label="Chat with Shikin"
          className="fixed z-[60] right-5 bottom-24 md:bottom-6 h-16 w-16 rounded-full bg-[#9c7a4d] text-white shadow-[0_10px_40px_rgba(156,122,77,0.45)] flex items-center justify-center hover:bg-[#0f172a] transition-all duration-300 hover:scale-105 group"
        >
          <MessageCircle className="h-7 w-7" />
          <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-emerald-400 border-2 border-[#f8fafc] animate-pulse" />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="shikin-panel"
          className="fixed z-[70] inset-0 md:inset-auto md:right-5 md:bottom-6 md:w-[400px] md:h-[620px] md:max-h-[85vh] bg-[#f8fafc] md:rounded-2xl md:shadow-[0_20px_70px_rgba(0,0,0,0.35)] border border-[#0f172a]/10 flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300"
        >
          {/* Header */}
          <div className="shrink-0 bg-[#0f172a] text-[#f8fafc] px-5 py-4 flex items-center gap-3">
            <div className="h-11 w-11 rounded-full bg-[#9c7a4d] flex items-center justify-center font-serif text-lg shrink-0">S</div>
            <div className="min-w-0 flex-1">
              <p className="font-serif text-lg leading-tight">Shikin</p>
              <p className="text-[0.7rem] uppercase tracking-[0.18em] text-[#c9b79c] flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Perth Tiling WA · Online
              </p>
            </div>
            <button data-testid="shikin-close" onClick={() => setOpen(false)} aria-label="Close chat" className="text-[#c9b79c] hover:text-white transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} data-lenis-prevent className="flex-1 overflow-y-auto px-4 py-5 space-y-4 overscroll-contain">
            {messages.map((m, i) => (
              <div key={i} data-testid={`shikin-msg-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[82%] px-4 py-2.5 text-[0.9rem] leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#9c7a4d] text-white rounded-2xl rounded-br-sm"
                      : "bg-white text-[#2f2b25] rounded-2xl rounded-bl-sm border border-[#0f172a]/5 shadow-sm"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white border border-[#0f172a]/5 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
                  <div className="flex gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-[#9c7a4d]/60 animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="h-2 w-2 rounded-full bg-[#9c7a4d]/60 animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="h-2 w-2 rounded-full bg-[#9c7a4d]/60 animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}

            {showQuick && (
              <div className="flex flex-wrap gap-2 pt-1">
                {QUICK_REPLIES.map((q, i) => (
                  <button
                    key={i}
                    data-testid={`shikin-quick-${i}`}
                    onClick={() => sendText(q)}
                    className="text-[0.8rem] px-3.5 py-2 rounded-full border border-[#9c7a4d]/40 text-[#7a5f38] bg-white/60 hover:bg-[#9c7a4d] hover:text-white hover:border-[#9c7a4d] transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            {uploading && (
              <div className="flex justify-end">
                <div className="text-[0.8rem] text-[#7a5f38] flex items-center gap-2 bg-white/70 px-3 py-2 rounded-xl border border-[#0f172a]/5">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading photos…
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="shrink-0 border-t border-[#0f172a]/10 bg-[#f8fafc] px-3 py-3">
            <div className="flex items-end gap-2">
              <button
                data-testid="shikin-upload"
                onClick={() => fileRef.current?.click()}
                disabled={uploading || sending}
                aria-label="Upload photos"
                className="shrink-0 h-11 w-11 rounded-full border border-[#0f172a]/15 text-[#7a5f38] hover:border-[#9c7a4d] hover:text-[#9c7a4d] disabled:opacity-50 flex items-center justify-center transition-colors"
              >
                <ImagePlus className="h-5 w-5" />
              </button>
              <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onUpload} />
              <textarea
                data-testid="shikin-input"
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendText(input); } }}
                placeholder="Type your message…"
                className="flex-1 resize-none max-h-28 bg-white border border-[#0f172a]/15 focus:border-[#9c7a4d] rounded-2xl px-4 py-2.5 text-[0.9rem] text-[#0f172a] placeholder:text-[#8a8378] outline-none transition-colors"
              />
              <button
                data-testid="shikin-send"
                onClick={() => sendText(input)}
                disabled={sending || !input.trim()}
                aria-label="Send message"
                className="shrink-0 h-11 w-11 rounded-full bg-[#9c7a4d] text-white hover:bg-[#0f172a] disabled:opacity-40 flex items-center justify-center transition-colors"
              >
                {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
              </button>
            </div>
            <p className="text-center text-[0.65rem] text-[#8a8378] mt-2 flex items-center justify-center gap-1">
              <Check className="h-3 w-3" /> A Perth Tiling WA specialist reviews every enquiry
            </p>
          </div>
        </div>
      )}
    </>
  );
}
